import { useNamedPresets } from '@/hooks/useNamedPresets'
import { normalizeDiscoveryDefaults } from '@/lib/screenerDiscoveryDefaults'
import type { ParseFilterChanges } from '@/hooks/useScreenerFilterDefaults'
import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import type { ScreenerDiscoveryFilters, ScreenerParseFilterDefaults, ScreenerParseFilters, User } from '@/types'

function selection(value: unknown): string[] | null {
  if (!Array.isArray(value) || !value.every((entry) => typeof entry === 'string')) return null
  return [...new Set(value.map((entry) => entry.trim().toLowerCase()).filter(Boolean))].sort()
}

function parseFilters(value: unknown): ScreenerParseFilters | null {
  if (!value || typeof value !== 'object') return null
  const item = value as Record<string, unknown>
  const exchanges = selection(item.exchanges)
  const markets = selection(item.markets)
  if (!exchanges || !markets || markets.some((market) => market !== 'spot' && market !== 'futures')) return null
  return { exchanges, markets }
}

function parseSnapshot(value: unknown): ScreenerParseFilterDefaults | null {
  if (!value || typeof value !== 'object') return null
  const item = value as Record<string, unknown>
  const chart_filters = parseFilters(item.chart_filters)
  const orderbook_filters = parseFilters(item.orderbook_filters)
  return chart_filters && orderbook_filters ? { chart_filters, orderbook_filters } : null
}

export function useScreenerFilterPresets({
  user, selectedExchanges, setSelectedExchanges, availableExchanges = selectedExchanges, chartFilters, orderbookFilters,
  updateParseFilters, parseDisabled, filters, setFilters, hydrated,
}: {
  user: User | null
  selectedExchanges: string[]
  setSelectedExchanges: (value: string[]) => void
  availableExchanges?: string[]
  chartFilters: ScreenerParseFilters
  orderbookFilters: ScreenerParseFilters
  updateParseFilters: (value: ParseFilterChanges) => Promise<boolean>
  parseDisabled: boolean
  filters: ScreenerDiscoveryFilters
  setFilters: Dispatch<SetStateAction<ScreenerDiscoveryFilters>>
  hydrated: boolean
}) {
  const username = user?.username ?? null
  const activeUsername = useRef(username)
  activeUsername.current = username
  const [displayIntent, setDisplayIntent] = useState<{ username: string | null; exchanges?: string[] }>({ username })
  const displayExchangesOverride = displayIntent.username === username ? displayIntent.exchanges : undefined
  useEffect(() => { setDisplayIntent({ username }) }, [username])

  const clearDisplayOverride = () => setDisplayIntent({ username })
  // Presets describe the user's desired sources, including exchanges which the
  // selected coin cannot currently show. The chart selection may be narrower.
  const displaySnapshot = displayExchangesOverride === undefined ? selectedExchanges : selection([
    ...displayExchangesOverride.filter((exchange) => !availableExchanges.includes(exchange)),
    ...selectedExchanges,
  ])!
  const onDisplayExchangesChange = (value: string[]) => {
    if (displayExchangesOverride !== undefined) {
      const exchanges = value.length === 0 ? [] : selection([
        ...displayExchangesOverride.filter((exchange) => !availableExchanges.includes(exchange)), ...value,
      ])!
      setDisplayIntent({ username, exchanges })
    }
    setSelectedExchanges(value)
  }
  const displayControls = useNamedPresets({
    user, preferenceKey: 'screener_display_presets', value: displaySnapshot,
    onApply: (value) => {
      setDisplayIntent({ username, exchanges: [...value] })
      setSelectedExchanges(value)
    }, normalize: selection, disabled: !hydrated,
  })
  const retainSavedDisplaySelection = async (save: () => Promise<boolean>) => {
    const snapshot = [...displaySnapshot]
    const ok = await save()
    if (ok && activeUsername.current === username) setDisplayIntent({ username, exchanges: snapshot })
    return ok
  }
  const display = {
    ...displayControls,
    onCreate: (name: string) => retainSavedDisplaySelection(() => displayControls.onCreate(name)),
    onUpdate: () => retainSavedDisplaySelection(displayControls.onUpdate),
  }
  const parse = useNamedPresets({
    user, preferenceKey: 'screener_parse_presets', value: { chart_filters: chartFilters, orderbook_filters: orderbookFilters },
    onApply: (value) => updateParseFilters({
      chartExchanges: value.chart_filters.exchanges, chartMarkets: value.chart_filters.markets,
      orderbookExchanges: value.orderbook_filters.exchanges, orderbookMarkets: value.orderbook_filters.markets,
    }), normalize: parseSnapshot, disabled: parseDisabled,
  })
  const discoveryExchanges = useNamedPresets({
    user, preferenceKey: 'screener_discovery_exchange_presets', value: filters.exchanges,
    onApply: (exchanges) => setFilters((current) => ({
      ...current, exchanges, minMatchingExchanges: Math.min(current.minMatchingExchanges, Math.max(1, exchanges.length)),
    })), normalize: selection, disabled: !hydrated,
  })
  const discovery = useNamedPresets({
    user, preferenceKey: 'screener_discovery_presets', value: normalizeDiscoveryDefaults(filters)!,
    onApply: (value) => setFilters((current) => ({
      ...current, ...value, minMatchingExchanges: Math.min(value.minMatchingExchanges, Math.max(1, current.exchanges.length)),
    })), normalize: normalizeDiscoveryDefaults, disabled: !hydrated,
  })
  return { display, parse, discoveryExchanges, discovery, displayExchangesOverride, clearDisplayOverride, onDisplayExchangesChange }
}
